import React, { useState, useCallback, useEffect } from 'react'
import { Button } from '@/race-tracker/components/ui/button'
import { Badge } from '@/race-tracker/components/ui/badge'
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useImportHistory,
  useBplCounts,
  uploadBplImportFile,
  parseBplImportFile,
  confirmBplImport,
  purgeBplData,
  bplQueryKeys,
  parseStationShareRows,
  importStationShareData,
  purgeStationShareData,
  parseAffiliateSheetRows,
  updateStationAffiliates,
} from '@/big-political-list/hooks/useBplData'
import { getBplUserName } from '@/big-political-list/lib/bplUser'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'

export default function ImportTab({ canWrite, isSuperAdmin, user, profile }) {
  const { data: importHistory = [] } = useImportHistory()
  const { data: bplCounts } = useBplCounts()
  const existingStationCount = bplCounts?.stationCount ?? 0
  const existingManagerCount = bplCounts?.managerCount ?? 0
  const qc = useQueryClient()

  const [file, setFile] = useState(null)
  const [parsing, setParsing] = useState(false)
  const [preview, setPreview] = useState(null)
  const [importing, setImporting] = useState(false)
  const [importMode, setImportMode] = useState(isSuperAdmin ? 'replace' : 'append')
  const [result, setResult] = useState(null)
  const [showBulkDelete, setShowBulkDelete] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [replaceConfirmText, setReplaceConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!isSuperAdmin && importMode === 'replace') {
      setImportMode('append')
    }
  }, [isSuperAdmin, importMode])

  const userName = getBplUserName(profile, user)

  const [bplDeleteResult, setBplDeleteResult] = useState(null) // null | 'success' | string (error msg)
  const [ssDeleteResult, setSsDeleteResult] = useState(null)   // null | 'success' | string (error msg)

  const handleBulkDelete = useCallback(async () => {
    if (!isSuperAdmin) return
    setDeleting(true)
    setBplDeleteResult(null)
    try {
      await purgeBplData()
      qc.invalidateQueries({ queryKey: bplQueryKeys.stations })
      qc.invalidateQueries({ queryKey: bplQueryKeys.managers })
      qc.invalidateQueries({ queryKey: bplQueryKeys.counts })
      setBplDeleteResult('success')
    } catch (err) {
      setBplDeleteResult(err.message || 'Delete failed')
    }
    setDeleting(false)
    setShowBulkDelete(false)
    setDeleteConfirmText('')
  }, [isSuperAdmin, qc])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0]
    if (f) {
      setFile(f)
      setPreview(null)
      setResult(null)
    }
  }, [])

  const handleParse = useCallback(async () => {
    if (!file || !canWrite) return
    setParsing(true)
    try {
      const storagePath = await uploadBplImportFile(file)
      const data = await parseBplImportFile(storagePath)
      if (data.error) {
        setPreview({ error: data.error })
      } else {
        setPreview({ stations: data.stations ?? [], managers: data.managers ?? [] })
      }
    } catch (err) {
      setPreview({ error: err.message || 'Parse failed' })
    }
    setParsing(false)
  }, [file, canWrite])

  const handleImport = useCallback(async () => {
    if (!preview || preview.error || !canWrite) return
    if (importMode === 'replace' && !isSuperAdmin) return
    const needsReplaceConfirm =
      importMode === 'replace' &&
      (preview.stations.length === 0 || preview.managers.length === 0)
    if (needsReplaceConfirm && replaceConfirmText !== 'REPLACE') return
    setImporting(true)
    try {
      const payload = await confirmBplImport({
        stations: preview.stations,
        managers: preview.managers,
        mode: importMode,
        filename: file?.name ?? 'import',
        user: userName,
        confirmWipeStations:
          importMode === 'replace' && preview.stations.length === 0,
      })
      qc.invalidateQueries({ queryKey: bplQueryKeys.stations })
      qc.invalidateQueries({ queryKey: bplQueryKeys.managers })
      qc.invalidateQueries({ queryKey: bplQueryKeys.counts })
      qc.invalidateQueries({ queryKey: bplQueryKeys.changeLogs })
      qc.invalidateQueries({ queryKey: bplQueryKeys.importHistory })
      setResult({
        success: true,
        stationCount: payload.stationCount,
        managerCount: payload.managerCount,
      })
      setPreview(null)
      setFile(null)
    } catch (err) {
      setResult({ error: err.message || 'Import failed' })
    }
    setImporting(false)
  }, [preview, importMode, file, canWrite, isSuperAdmin, userName, replaceConfirmText, qc])

  const resetImportFlow = useCallback(() => {
    setFile(null)
    setPreview(null)
    setResult(null)
    setReplaceConfirmText('')
  }, [])

  // ── Station Share import state ───────────────────────────────────────────
  const [ssFile, setSsFile] = useState(null)
  const [ssParsing, setSsParsing] = useState(false)
  const [ssPreview, setSsPreview] = useState(null)
  const [ssImporting, setSsImporting] = useState(false)
  const [ssResult, setSsResult] = useState(null)
  const [ssImportMode, setSsImportMode] = useState(isSuperAdmin ? 'replace' : 'append')
  const [ssDeleteConfirmText, setSsDeleteConfirmText] = useState('')
  const [showSsDelete, setShowSsDelete] = useState(false)
  const [ssDeletingSs, setSsDeletingSs] = useState(false)

  useEffect(() => {
    if (!isSuperAdmin && ssImportMode === 'replace') setSsImportMode('append')
  }, [isSuperAdmin, ssImportMode])

  // ── Affiliate Reference update state ────────────────────────────────────────
  const [affiliateFile, setAffiliateFile] = useState(null)
  const [affiliateUpdating, setAffiliateUpdating] = useState(false)
  const [affiliateResult, setAffiliateResult] = useState(null) // null | { entriesUpdated, stationsUpdated } | string (error)

  const handleAffiliateDrop = useCallback((e) => {
    e.preventDefault()
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0]
    if (f) { setAffiliateFile(f); setAffiliateResult(null) }
  }, [])

  const resetAffiliateFlow = useCallback(() => {
    setAffiliateFile(null)
    setAffiliateResult(null)
  }, [])

  const handleAffiliateUpdate = useCallback(async () => {
    if (!affiliateFile || !canWrite) return
    setAffiliateUpdating(true)
    setAffiliateResult(null)
    try {
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.2/package/xlsx.mjs')
      const arrayBuffer = await affiliateFile.arrayBuffer()
      const wb = XLSX.read(arrayBuffer, { type: 'array' })
      const rawRows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])
      if (!rawRows.length) {
        setAffiliateResult('No data rows found in the file.')
        setAffiliateUpdating(false)
        return
      }
      const affiliateMap = parseAffiliateSheetRows(rawRows)
      if (Object.keys(affiliateMap).length === 0) {
        setAffiliateResult('No valid Station/Affiliate pairs found. Expected columns: Station, Affiliate.')
        setAffiliateUpdating(false)
        return
      }
      const result = await updateStationAffiliates(affiliateMap)
      setAffiliateResult(result)
    } catch (err) {
      setAffiliateResult(err.message || 'Update failed')
    }
    setAffiliateUpdating(false)
  }, [affiliateFile, canWrite])

  const handleSsDrop = useCallback((e) => {
    e.preventDefault()
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0]
    if (f) { setSsFile(f); setSsPreview(null); setSsResult(null) }
  }, [])

  const handleSsParse = useCallback(async () => {
    if (!ssFile || !canWrite) return
    setSsParsing(true)
    try {
      // Load SheetJS from CDN to parse the .xlsx binary in the browser
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.2/package/xlsx.mjs')
      const arrayBuffer = await ssFile.arrayBuffer()
      const wb = XLSX.read(arrayBuffer, { type: 'array' })
      const sheetName = wb.SheetNames[0]
      const rawRows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName])
      if (!rawRows.length) {
        setSsPreview({ error: 'No data rows found in the file.' })
        setSsParsing(false)
        return
      }
      // Validate required columns
      const first = rawRows[0]
      const keys = Object.keys(first).map(k => k.toLowerCase())
      if (!keys.includes('state') || !keys.includes('market') || !keys.includes('shares')) {
        setSsPreview({ error: `Missing required columns. Found: ${Object.keys(first).join(', ')}. Expected: State, Market, Shares.` })
        setSsParsing(false)
        return
      }
      // Build affiliate lookup from bpl_stations so imported entries get affiliate auto-filled
      let affiliateMap = {}
      const { data: stations } = await supabase
        .from('bpl_stations')
        .select('station, affiliate')
      if (stations) {
        for (const s of stations) {
          if (s.station && s.affiliate) {
            affiliateMap[s.station.toUpperCase()] = s.affiliate
          }
        }
      }
      const parsed = parseStationShareRows(rawRows, affiliateMap)
      const entryCount = parsed.entries.reduce((s, g) => s + g.length, 0)
      const noteCount = parsed.notes.reduce((s, g) => s + g.length, 0)
      setSsPreview({ parsed, marketCount: parsed.markets.length, entryCount, noteCount })
    } catch (err) {
      setSsPreview({ error: err.message || 'Parse failed' })
    }
    setSsParsing(false)
  }, [ssFile, canWrite])

  const handleSsImport = useCallback(async () => {
    if (!ssPreview?.parsed || !canWrite) return
    if (ssImportMode === 'replace' && !isSuperAdmin) return
    setSsImporting(true)
    try {
      const stats = await importStationShareData(
        ssPreview.parsed, ssImportMode, qc, userName, ssFile?.name ?? 'station_share.xlsx'
      )
      setSsResult(stats)
      setSsPreview(null)
      setSsFile(null)
    } catch (err) {
      setSsResult({ error: err.message || 'Import failed' })
    }
    setSsImporting(false)
  }, [ssPreview, ssImportMode, canWrite, isSuperAdmin, qc, userName, ssFile])

  const resetSsFlow = useCallback(() => {
    setSsFile(null)
    setSsPreview(null)
    setSsResult(null)
  }, [])

  const handleSsDelete = useCallback(async () => {
    if (!isSuperAdmin) return
    setSsDeletingSs(true)
    setSsDeleteResult(null)
    try {
      await purgeStationShareData(qc)
      setSsDeleteResult('success')
    } catch (err) {
      setSsDeleteResult(err.message || 'Delete failed')
    }
    setSsDeletingSs(false)
    setShowSsDelete(false)
    setSsDeleteConfirmText('')
  }, [isSuperAdmin, qc])

  const showUpload = !file && !preview && !result
  const showParse = file && !preview && !result && !parsing
  const showParsing = parsing && file && !preview && !result
  const showPreview = preview && !preview.error
  const showError = Boolean(preview?.error)
  const showImportError = Boolean(result?.error)
  const showSuccess = Boolean(result?.success)

  const replaceNeedsTyping =
    importMode === 'replace' &&
    showPreview &&
    (preview.stations.length === 0 || preview.managers.length === 0)

  const cardBorderClass = showUpload
    ? 'border-dashed hover:border-primary/40'
    : 'border-solid border-primary/30'

  const fileInput = (
    <input
      id="bpl-import-file-input"
      type="file"
      accept=".xlsx,.csv,.xls"
      onChange={handleDrop}
      className="hidden"
      disabled={!canWrite || parsing}
    />
  )

  const fileNameRow = file ? (
    <div className="flex items-center justify-center gap-2 flex-wrap">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full">
        <FileSpreadsheet className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium">{file.name}</span>
      </div>
      {!parsing && canWrite && (
        <label htmlFor="bpl-import-file-input" className="text-xs text-primary font-medium cursor-pointer hover:underline">
          Change file
        </label>
      )}
    </div>
  ) : null

  // Station Share card display flags
  const ssShowUpload = !ssFile && !ssPreview && !ssResult
  const ssShowParse = ssFile && !ssPreview && !ssResult && !ssParsing
  const ssShowParsing = ssParsing && ssFile && !ssPreview && !ssResult
  const ssShowPreview = ssPreview && !ssPreview.error
  const ssShowError = Boolean(ssPreview?.error)
  const ssShowSuccess = Boolean(ssResult && !ssResult.error)
  const ssShowImportError = Boolean(ssResult?.error)
  const ssBorderClass = ssShowUpload ? 'border-dashed hover:border-primary/40' : 'border-solid border-primary/30'

  const ssFileInput = (
    <input
      id="ss-import-file-input"
      type="file"
      accept=".xlsx,.xls"
      onChange={handleSsDrop}
      className="hidden"
      disabled={!canWrite || ssParsing}
    />
  )

  const ssFileNameRow = ssFile ? (
    <div className="flex items-center justify-center gap-2 flex-wrap">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full">
        <FileSpreadsheet className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium">{ssFile.name}</span>
      </div>
      {!ssParsing && canWrite && (
        <label htmlFor="ss-import-file-input" className="text-xs text-primary font-medium cursor-pointer hover:underline">
          Change file
        </label>
      )}
    </div>
  ) : null

  // Affiliate card display flags
  const affiliateShowUpload   = !affiliateFile && !affiliateResult
  const affiliateShowReady    = affiliateFile && !affiliateUpdating && !affiliateResult
  const affiliateShowUpdating = affiliateUpdating
  const affiliateShowSuccess  = affiliateResult && typeof affiliateResult === 'object'
  const affiliateShowError    = affiliateResult && typeof affiliateResult === 'string'
  const affiliateBorderClass  = affiliateShowUpload
    ? 'border-dashed hover:border-primary/40'
    : 'border-solid border-primary/30'

  const affiliateFileInput = (
    <input
      id="affiliate-file-input"
      type="file"
      accept=".xlsx,.xls,.csv"
      onChange={handleAffiliateDrop}
      className="hidden"
      disabled={affiliateUpdating}
    />
  )

  const affiliateFileNameRow = affiliateFile ? (
    <div className="flex items-center justify-center gap-2 flex-wrap">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full">
        <FileSpreadsheet className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium">{affiliateFile.name}</span>
      </div>
      {!affiliateUpdating && (
        <label htmlFor="affiliate-file-input" className="text-xs text-primary font-medium cursor-pointer hover:underline">
          Change file
        </label>
      )}
    </div>
  ) : null

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold mb-3">Big Political List Data</h3>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className={`border-2 rounded-lg p-8 bg-card transition-colors ${cardBorderClass} ${
          showUpload ? 'text-center' : ''
        }`}
      >
        {fileInput}

        {showUpload && (
          <>
            <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium mb-1">Drag & drop your file here</p>
            <p className="text-xs text-muted-foreground mb-3">Accepts .xlsx and .csv files</p>
            <label htmlFor="bpl-import-file-input" className="inline-block">
              <span
                className={`px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg transition-colors ${
                  canWrite ? 'cursor-pointer hover:bg-primary/90' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                Choose File
              </span>
            </label>
          </>
        )}

        {showParsing && (
          <div className="space-y-4 max-w-md mx-auto">
            {fileNameRow}
            <div className="flex flex-col items-center gap-2 py-2">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm font-medium text-muted-foreground">Parsing…</p>
            </div>
          </div>
        )}

        {showParse && (
          <div className="space-y-5 max-w-md mx-auto">
            {fileNameRow}
            <Button
              onClick={handleParse}
              disabled={!canWrite}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Parse & Preview
            </Button>
          </div>
        )}

        {showPreview && (
          <div className="space-y-4 max-w-lg mx-auto text-left">
            {fileNameRow && <div className="flex justify-center pb-1">{fileNameRow}</div>}
            <h3 className="text-sm font-semibold text-center">Import Preview</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground">Stations to import</p>
                <p className="text-2xl font-bold">{preview.stations.length.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground">Managers to import</p>
                <p className="text-2xl font-bold">{preview.managers.length}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <label
                className={`flex items-center gap-2 text-sm ${isSuperAdmin ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                title={!isSuperAdmin ? 'Super admin only' : undefined}
              >
                <input
                  type="radio"
                  checked={importMode === 'replace'}
                  onChange={() => isSuperAdmin && setImportMode('replace')}
                  disabled={!isSuperAdmin}
                  className="accent-primary"
                />
                Replace all (clear existing data first)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  checked={importMode === 'append'}
                  onChange={() => {
                    setImportMode('append')
                    setReplaceConfirmText('')
                  }}
                  disabled={!canWrite}
                  className="accent-primary"
                />
                Append (add to existing data)
              </label>
            </div>

            {importMode === 'replace' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-left">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  Replace deletes all existing stations and managers before importing this file.
                </p>
              </div>
            )}

            {importMode === 'replace' && preview.stations.length === 0 && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 flex items-start gap-2 text-left">
                <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-xs text-destructive">
                  This file has no station rows. Replace will remove all{' '}
                  {existingStationCount.toLocaleString()} station
                  {existingStationCount === 1 ? '' : 's'} currently in the database.
                </p>
              </div>
            )}

            {importMode === 'replace' && preview.managers.length === 0 && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 flex items-start gap-2 text-left">
                <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-xs text-destructive">
                  This file has no manager rows. Replace will remove all{' '}
                  {existingManagerCount.toLocaleString()} manager
                  {existingManagerCount === 1 ? '' : 's'} currently in the database.
                </p>
              </div>
            )}

            {replaceNeedsTyping && (
              <div className="rounded-lg border border-destructive/40 bg-white p-3 space-y-2 text-left">
                <p className="text-xs font-medium">
                  Type <span className="font-mono font-bold">REPLACE</span> to confirm this import:
                </p>
                <input
                  type="text"
                  value={replaceConfirmText}
                  onChange={(e) => setReplaceConfirmText(e.target.value)}
                  placeholder="Type REPLACE"
                  className="w-full border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-destructive/50"
                />
              </div>
            )}

            <div className="flex gap-2 justify-center">
              <Button
                onClick={handleImport}
                disabled={
                  importing ||
                  !canWrite ||
                  (importMode === 'replace' && !isSuperAdmin) ||
                  (replaceNeedsTyping && replaceConfirmText !== 'REPLACE')
                }
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…
                  </>
                ) : (
                  'Confirm Import'
                )}
              </Button>
              <Button variant="outline" onClick={resetImportFlow}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {showError && (
          <div className="space-y-4 max-w-md mx-auto">
            {fileNameRow}
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3 text-left">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-destructive">Parse Error</p>
                <p className="text-xs text-muted-foreground mt-1">{preview.error}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={() => setPreview(null)}>
              Try again
            </Button>
          </div>
        )}

        {showImportError && (
          <div className="space-y-4 max-w-md mx-auto">
            {fileNameRow}
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3 text-left">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-destructive">Import failed</p>
                <p className="text-xs text-muted-foreground mt-1">{result.error}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={resetImportFlow}>
              Try again
            </Button>
          </div>
        )}

        {showSuccess && (
          <div className="space-y-4 max-w-md mx-auto">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3 text-left">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-emerald-700">Import Complete</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {result.stationCount.toLocaleString()} stations and {result.managerCount} managers imported successfully.
                </p>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={resetImportFlow}>
              Import another file
            </Button>
          </div>
        )}
      </div>
      </div>

      {/* ── Station Share import card ────────────────────────────────────── */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Station Share Data</h3>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleSsDrop}
          className={`border-2 rounded-lg p-8 bg-card transition-colors ${ssBorderClass} ${ssShowUpload ? 'text-center' : ''}`}
        >
          {ssFileInput}

          {ssShowUpload && (
            <>
              <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium mb-1">Drag & drop station_share.xlsx here</p>
              <p className="text-xs text-muted-foreground mb-3">Accepts the Station Share Excel export (State / Market / Shares format)</p>
              <label htmlFor="ss-import-file-input" className="inline-block">
                <span className={`px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg transition-colors ${canWrite ? 'cursor-pointer hover:bg-primary/90' : 'opacity-50 cursor-not-allowed'}`}>
                  Choose File
                </span>
              </label>
            </>
          )}

          {ssShowParsing && (
            <div className="space-y-4 max-w-md mx-auto">
              {ssFileNameRow}
              <div className="flex flex-col items-center gap-2 py-2">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm font-medium text-muted-foreground">Parsing…</p>
              </div>
            </div>
          )}

          {ssShowParse && (
            <div className="space-y-5 max-w-md mx-auto">
              {ssFileNameRow}
              <Button onClick={handleSsParse} disabled={!canWrite} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                Parse & Preview
              </Button>
            </div>
          )}

          {ssShowPreview && (
            <div className="space-y-4 max-w-lg mx-auto text-left">
              {ssFileNameRow && <div className="flex justify-center pb-1">{ssFileNameRow}</div>}
              <h3 className="text-sm font-semibold text-center">Import Preview</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Markets</p>
                  <p className="text-2xl font-bold">{ssPreview.marketCount.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Station Entries</p>
                  <p className="text-2xl font-bold">{ssPreview.entryCount.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Buy-Rule Notes</p>
                  <p className="text-2xl font-bold">{ssPreview.noteCount}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className={`flex items-center gap-2 text-sm ${isSuperAdmin ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`} title={!isSuperAdmin ? 'Super admin only' : undefined}>
                  <input type="radio" checked={ssImportMode === 'replace'} onChange={() => isSuperAdmin && setSsImportMode('replace')} disabled={!isSuperAdmin} className="accent-primary" />
                  Replace all (clear existing first)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={ssImportMode === 'append'} onChange={() => setSsImportMode('append')} disabled={!canWrite} className="accent-primary" />
                  Append (skip existing markets)
                </label>
              </div>

              {ssImportMode === 'replace' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-left">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-900">Replace deletes all existing station share markets, entries, and notes before importing.</p>
                </div>
              )}

              <div className="flex gap-2 justify-center">
                <Button
                  onClick={handleSsImport}
                  disabled={ssImporting || !canWrite || (ssImportMode === 'replace' && !isSuperAdmin)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {ssImporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : 'Confirm Import'}
                </Button>
                <Button variant="outline" onClick={resetSsFlow}>Cancel</Button>
              </div>
            </div>
          )}

          {ssShowError && (
            <div className="space-y-4 max-w-md mx-auto">
              {ssFileNameRow}
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3 text-left">
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-destructive">Parse Error</p>
                  <p className="text-xs text-muted-foreground mt-1">{ssPreview.error}</p>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setSsPreview(null)}>Try again</Button>
            </div>
          )}

          {ssShowImportError && (
            <div className="space-y-4 max-w-md mx-auto">
              {ssFileNameRow}
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3 text-left">
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-destructive">Import failed</p>
                  <p className="text-xs text-muted-foreground mt-1">{ssResult.error}</p>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={resetSsFlow}>
                Try again
              </Button>
            </div>
          )}

          {ssShowSuccess && (
            <div className="space-y-4 max-w-md mx-auto">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3 text-left">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-emerald-700">Import Complete</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {ssResult.marketCount.toLocaleString()} markets, {ssResult.entryCount.toLocaleString()} entries
                    {ssResult.noteCount > 0 ? `, and ${ssResult.noteCount} buy-rule note${ssResult.noteCount !== 1 ? 's' : ''}` : ''} imported successfully.
                  </p>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={resetSsFlow}>Import another file</Button>
            </div>
          )}
        </div>
      </div>

      {/* ── Update Affiliate Reference Data ─────────────────────────── */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Update Affiliate Reference Data</h3>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleAffiliateDrop}
          className={`border-2 rounded-lg p-8 bg-card transition-colors ${affiliateBorderClass} ${affiliateShowUpload ? 'text-center' : ''}`}
        >
          {affiliateFileInput}

          {affiliateShowUpload && (
            <>
              <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium mb-1">Drag & drop Station-Affiliate-Market sheet here</p>
              <p className="text-xs text-muted-foreground mb-3">
                Populates affiliate (NBC, CBS, ABC, FOX…) on all existing Station Share entries and the BPL station list.
                Expected columns: <code className="font-mono bg-muted px-1 rounded">Station</code>,{' '}
                <code className="font-mono bg-muted px-1 rounded">Affiliate</code>,{' '}
                <code className="font-mono bg-muted px-1 rounded">Market</code>.
              </p>
              <label htmlFor="affiliate-file-input" className="inline-block">
                <span className={`px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg transition-colors ${canWrite ? 'cursor-pointer hover:bg-primary/90' : 'opacity-50 cursor-not-allowed'}`}>
                  Choose File
                </span>
              </label>
            </>
          )}

          {affiliateShowReady && (
            <div className="space-y-5 max-w-md mx-auto">
              {affiliateFileNameRow}
              <Button
                onClick={handleAffiliateUpdate}
                disabled={!canWrite}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Update Affiliates
              </Button>
            </div>
          )}

          {affiliateShowUpdating && (
            <div className="space-y-4 max-w-md mx-auto">
              {affiliateFileNameRow}
              <div className="flex flex-col items-center gap-2 py-2">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm font-medium text-muted-foreground">Updating…</p>
              </div>
            </div>
          )}

          {affiliateShowSuccess && (
            <div className="space-y-4 max-w-md mx-auto">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3 text-left">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-emerald-700">Affiliates Updated</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {affiliateResult.entriesUpdated.toLocaleString()} station share entries updated
                    {affiliateResult.stationsUpdated > 0
                      ? ` · ${affiliateResult.stationsUpdated.toLocaleString()} BPL stations updated`
                      : ''}.
                  </p>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={resetAffiliateFlow}>
                Upload another file
              </Button>
            </div>
          )}

          {affiliateShowError && (
            <div className="space-y-4 max-w-md mx-auto">
              {affiliateFileNameRow}
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3 text-left">
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-destructive">Update Error</p>
                  <p className="text-xs text-muted-foreground mt-1">{affiliateResult}</p>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setAffiliateResult(null)}>
                Try again
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border bg-card p-5 space-y-4">
        <h3 className="text-sm font-semibold">Required Columns</h3>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Stations Sheet</p>
            <div className="space-y-1">
              {[
                { col: 'state', req: true, note: 'e.g. AL or AL/GA for multi-state' },
                { col: 'market', req: true, note: 'DMA name' },
                { col: 'station', req: true, note: 'Call sign' },
                { col: 'affiliate', req: false, note: 'CBS, FOX, NBC…' },
                { col: 'rep_firm', req: false },
                { col: 'rep', req: false },
                { col: 'rep_phone', req: false },
                { col: 'rep_email', req: false },
                { col: 'ownership', req: false },
                { col: 'cd_contact_name', req: false },
                { col: 'cd_contact_title', req: false },
                { col: 'cd_contact_phone', req: false },
                { col: 'cd_contact_email', req: false },
                { col: 'station_address', req: false },
                { col: 'station_city', req: false },
                { col: 'station_state', req: false },
                { col: 'station_zip', req: false },
              ].map(({ col, req, note }) => (
                <div key={col} className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${req ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                  <code className="text-xs font-mono text-foreground">{col}</code>
                  {req && <span className="text-[10px] font-semibold text-primary">required</span>}
                  {note && <span className="text-[10px] text-muted-foreground">— {note}</span>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Managers Sheet</p>
            <div className="space-y-1">
              {[
                { col: 'rep_firm', req: true, note: 'must match station rep_firm values' },
                { col: 'manager', req: true, note: 'full name' },
                { col: 'title', req: false },
                { col: 'email', req: false },
                { col: 'office_phone', req: false },
                { col: 'contact_mobile', req: false },
              ].map(({ col, req, note }) => (
                <div key={col} className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${req ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                  <code className="text-xs font-mono text-foreground">{col}</code>
                  {req && <span className="text-[10px] font-semibold text-primary">required</span>}
                  {note && <span className="text-[10px] text-muted-foreground">— {note}</span>}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 italic">
              Tip: Place stations and managers on separate sheets, or the AI parser will auto-detect both from a single sheet.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Import stations and managers together (two sheets), or import stations first then managers with{' '}
              <strong>Append</strong> — not Replace on the second file.
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Station Share File</p>
            <div className="space-y-1">
              {[
                { col: 'State', req: true, note: 'state abbreviation' },
                { col: 'Market', req: true, note: 'DMA name' },
                { col: 'Shares', req: true, note: 'e.g. KXAS-35% WFAA-28%' },
              ].map(({ col, req, note }) => (
                <div key={col} className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${req ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                  <code className="text-xs font-mono text-foreground">{col}</code>
                  {req && <span className="text-[10px] font-semibold text-primary">required</span>}
                  {note && <span className="text-[10px] text-muted-foreground">— {note}</span>}
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-1.5 text-[11px] text-muted-foreground">
              <p><code className="font-mono bg-muted px-1 rounded">**CALL-NN%**</code> — deal station (bold in UI)</p>
              <p>Trailing <code className="font-mono bg-muted px-1 rounded">(text)</code> — saved as buy-rule note</p>
              <p className="mt-2 italic">Upload the <strong>station_share.xlsx</strong> export directly — no reformatting needed.</p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-3">Import History</h3>
        {importHistory.length === 0 ? (
          <p className="text-sm text-muted-foreground">No imports yet.</p>
        ) : (
          <div className="rounded-lg border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">File</th>
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="px-4 py-2 text-center">Type</th>
                  <th className="px-4 py-2 text-center">Mode</th>
                  <th className="px-4 py-2 text-right">Count 1</th>
                  <th className="px-4 py-2 text-right">Count 2</th>
                  <th className="px-4 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {importHistory.map((h) => {
                  const isSs = h.import_type === 'station_share'
                  return (
                    <tr key={h.id} className="hover:bg-muted/20">
                      <td className="px-4 py-2 text-xs">
                        {h.timestamp ? format(new Date(h.timestamp), 'MMM d, yyyy h:mm a') : '—'}
                      </td>
                      <td className="px-4 py-2 font-medium">{h.filename}</td>
                      <td className="px-4 py-2 text-muted-foreground">{h.user}</td>
                      <td className="px-4 py-2 text-center">
                        <Badge
                          variant="outline"
                          className={
                            isSs
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-muted/50 text-muted-foreground'
                          }
                        >
                          {isSs ? 'Station Share' : 'BPL'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Badge
                          variant="outline"
                          className={
                            h.import_mode === 'replace'
                              ? 'bg-amber-50 text-amber-800 border-amber-200'
                              : 'bg-muted/50 text-muted-foreground'
                          }
                        >
                          {h.import_mode === 'replace' ? 'replace' : 'append'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        <span className="text-[10px] text-muted-foreground mr-1">{isSs ? 'markets' : 'stations'}</span>
                        {h.station_count?.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        <span className="text-[10px] text-muted-foreground mr-1">{isSs ? 'entries' : 'managers'}</span>
                        {h.manager_count}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Badge
                          variant="outline"
                          className={
                            h.status === 'success'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : h.status === 'partial'
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-red-50 text-red-700 border-red-200'
                          }
                        >
                          {h.status}
                        </Badge>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-destructive">Danger Zone</h3>

        {/* BPL stations + managers delete */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-foreground">Delete All BPL Data</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Permanently delete all stations and rep firm managers from the database.
            </p>
          </div>
          {!showBulkDelete && (
            <Button
              variant="outline"
              size="sm"
              className="border-destructive/50 text-destructive hover:bg-destructive hover:text-white shrink-0"
              onClick={() => isSuperAdmin && setShowBulkDelete(true)}
              disabled={!isSuperAdmin}
              title={!isSuperAdmin ? 'Super admin only' : undefined}
            >
              Delete All Data
            </Button>
          )}
        </div>

        {showBulkDelete && (
          <div className="rounded-lg border border-destructive/40 bg-white p-4 space-y-3">
            <p className="text-sm font-medium">
              To confirm, type <span className="font-mono font-bold">DELETE ALL</span> below:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE ALL"
              className="w-full border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-destructive/50"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={deleteConfirmText !== 'DELETE ALL' || deleting || !isSuperAdmin}
                className="bg-destructive text-white hover:bg-destructive/90 disabled:opacity-40"
                onClick={handleBulkDelete}
              >
                {deleting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Deleting…</> : 'Confirm Delete'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowBulkDelete(false); setDeleteConfirmText('') }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {bplDeleteResult === 'success' && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <p className="text-xs text-emerald-700 font-medium">All BPL stations and managers deleted successfully.</p>
            </div>
            <button onClick={() => setBplDeleteResult(null)} className="text-xs text-emerald-600 hover:underline shrink-0">Dismiss</button>
          </div>
        )}
        {bplDeleteResult && bplDeleteResult !== 'success' && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
              <p className="text-xs text-destructive font-medium">{bplDeleteResult}</p>
            </div>
            <button onClick={() => setBplDeleteResult(null)} className="text-xs text-destructive hover:underline shrink-0">Dismiss</button>
          </div>
        )}

        <div className="border-t border-destructive/20 pt-3" />

        {/* Station Share delete */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-foreground">Delete All Station Share Data</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Permanently removes all station share markets, entries, and notes.
            </p>
          </div>
          {!showSsDelete && (
            <Button
              variant="outline"
              size="sm"
              className="border-destructive/50 text-destructive hover:bg-destructive hover:text-white shrink-0"
              onClick={() => isSuperAdmin && setShowSsDelete(true)}
              disabled={!isSuperAdmin}
              title={!isSuperAdmin ? 'Super admin only' : undefined}
            >
              Delete Station Share
            </Button>
          )}
        </div>

        {showSsDelete && (
          <div className="rounded-lg border border-destructive/40 bg-white p-4 space-y-3">
            <p className="text-sm font-medium">
              To confirm, type <span className="font-mono font-bold">DELETE ALL</span> below:
            </p>
            <input
              type="text"
              value={ssDeleteConfirmText}
              onChange={(e) => setSsDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE ALL"
              className="w-full border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-destructive/50"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={ssDeleteConfirmText !== 'DELETE ALL' || ssDeletingSs || !isSuperAdmin}
                className="bg-destructive text-white hover:bg-destructive/90 disabled:opacity-40"
                onClick={handleSsDelete}
              >
                {ssDeletingSs ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Deleting…</> : 'Confirm Delete'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowSsDelete(false); setSsDeleteConfirmText('') }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {ssDeleteResult === 'success' && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <p className="text-xs text-emerald-700 font-medium">All station share data deleted successfully.</p>
            </div>
            <button onClick={() => setSsDeleteResult(null)} className="text-xs text-emerald-600 hover:underline shrink-0">Dismiss</button>
          </div>
        )}
        {ssDeleteResult && ssDeleteResult !== 'success' && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
              <p className="text-xs text-destructive font-medium">{ssDeleteResult}</p>
            </div>
            <button onClick={() => setSsDeleteResult(null)} className="text-xs text-destructive hover:underline shrink-0">Dismiss</button>
          </div>
        )}
      </div>
    </div>
  )
}
